import { FileDoneOutlined, ReloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { BillingOperation, BillingOperationCharge, BillingOperationsRegister, BillingOperationsResponse } from "@wms/domain";
import { Alert, App, Button, DatePicker, Empty, Space, Table, Tag, Tooltip, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { amountsWords, formatMoney, operationsWord, selectedAmounts } from "../api/billing";
import { api, describeError } from "../api/client";
import { formatDate } from "../api/format";
import { useAuthStore } from "../auth/store";
import { LoadProblem } from "../components/LoadProblem";
import { OperationTag, operationOf } from "../components/OperationTag";
import { PageHeader } from "../components/PageHeader";
import { RefSelect } from "../components/RefSelect";

const STATE: Record<BillingOperationCharge["state"], { label: string; color: string }> = {
  new: { label: "Будет начислено", color: "blue" },
  recorded: { label: "В журнале услуг", color: "green" },
  in_register: { label: "В реестре", color: "default" },
  pending: { label: "Нужно уточнить", color: "orange" },
  other_client: { label: "Другой клиент", color: "red" },
  cancelled: { label: "Отменено", color: "default" },
};

function newKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `ops-${Date.now()}-${Math.random()}`;
}

/** The services of one operation: what is charged, at which tariff version of which day, or why not. */
function Charges({ row }: { row: BillingOperation }) {
  return (
    <Table<BillingOperationCharge>
      size="small"
      rowKey={(c) => `${c.service_id}:${c.date}:${c.unit_code}`}
      pagination={false}
      dataSource={row.charges}
      data-testid={`billing-op-charges-${row.number ?? row.key}`}
      locale={{ emptyText: row.reason ?? "Начислений нет" }}
      columns={[
        { title: "Услуга", dataIndex: "service_name" },
        { title: "Дата операции", dataIndex: "date", render: (v: string) => formatDate(v) },
        { title: "Кол-во", dataIndex: "quantity", align: "right" as const, render: (v: string, c) => `${v} ${c.unit_label.toLowerCase()}` },
        { title: "Тип обработки", dataIndex: "handling_label" },
        {
          title: "Ставка",
          dataIndex: "rate",
          align: "right" as const,
          render: (v: string | null, c) =>
            v ? (
              <Tooltip title={c.formula ?? undefined}>
                <span>
                  {formatMoney(v)} {c.currency}
                  {c.tariff_label && (
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                      {c.tariff_label}
                    </Typography.Text>
                  )}
                </span>
              </Tooltip>
            ) : (
              "—"
            ),
        },
        { title: "Без налога", dataIndex: "net", align: "right" as const, render: (v: string | null) => (v ? formatMoney(v) : "—") },
        { title: "Налог", dataIndex: "tax", align: "right" as const, render: (v: string | null, c) => (v ? formatMoney(v) : c.tax_label) },
        { title: "С налогом", dataIndex: "gross", align: "right" as const, render: (v: string | null) => (v ? <b>{formatMoney(v)}</b> : "—") },
        {
          title: "Состояние",
          dataIndex: "state",
          render: (v: BillingOperationCharge["state"], c) => (
            <Space direction="vertical" size={2}>
              <Tag color={STATE[v]?.color}>
                {v === "in_register" && c.register ? <Link to={`/service_registers/${c.register.id}`}>В реестре {c.register.number}</Link> : STATE[v]?.label ?? v}
              </Tag>
              {c.reason && v !== "in_register" && <Typography.Text type={v === "pending" ? "warning" : "secondary"}>{c.reason}</Typography.Text>}
              {c.event_id && v !== "in_register" && <Link to={`/service_events/${c.event_id}`}>Начисление в журнале</Link>}
            </Space>
          ),
        },
      ]}
    />
  );
}

/**
 * UX3010-09 (30.10.2026) «Операции к расчёту»: the administrator chooses a client and a period, sees the completed
 * operations of exactly this client — how much was handled and how (pallets, boxes, pieces, kilograms), what each
 * service costs at the client's tariff of the day of the operation — ticks the ones to bill and forms the register;
 * «Утвердить реестр» and «Создать счёт» go on in the register card as before. The server recalculates everything
 * on the click: the page only proposes. `?register_id=` shows the operations behind an existing register.
 */
export function BillingOperationsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const [params, setParams] = useSearchParams();
  const partner = params.get("partner_id") ?? undefined;
  const registerId = params.get("register_id") ?? undefined;
  const [period, setPeriod] = useState<[Dayjs, Dayjs]>([dayjs().startOf("month"), dayjs()]);
  const [warehouse, setWarehouse] = useState<string | undefined>();
  const [selected, setSelected] = useState<string[]>([]);
  const [requestKey, setRequestKey] = useState(newKey);
  const query = useMemo(
    () =>
      registerId
        ? { register_id: registerId }
        : { partner_id: partner, date_from: period[0].format("YYYY-MM-DD"), date_to: period[1].format("YYYY-MM-DD"), warehouse_id: warehouse },
    [registerId, partner, period, warehouse],
  );
  const list = useQuery({
    queryKey: ["billing-operations", query],
    queryFn: () => api.get<BillingOperationsResponse>("/billing/operations", query),
  });
  // a new choice is a new request; a retry of the same choice (lost answer) keeps the key and gets the same register
  useEffect(() => setRequestKey(newKey()), [selected, partner, warehouse, period]);
  useEffect(() => setSelected([]), [partner, warehouse, period, registerId]);

  const items = list.data?.items ?? [];
  const chosen = selected.filter((key) => items.some((i) => i.key === key && i.selectable));
  const chosenAmounts = selectedAmounts(items, chosen);
  const canRegister = can("billing.registers.write");
  const form = useMutation({
    mutationFn: () =>
      api.post<BillingOperationsRegister>("/billing/operations/register", {
        partner_id: partner,
        operations: chosen,
        warehouse_id: warehouse,
        date_from: period[0].format("YYYY-MM-DD"),
        date_to: period[1].format("YYYY-MM-DD"),
        request_key: requestKey,
      }),
    onSuccess: (r) => {
      message.success(
        r.warnings.length
          ? `Реестр ${r.number} сформирован: ${r.lines} строк на ${formatMoney(r.total_gross)} ${r.currency}; ${r.warnings.length} начислений требуют уточнения`
          : `Реестр ${r.number} сформирован: ${r.lines} строк на ${formatMoney(r.total_gross)} ${r.currency}. Проверьте и утвердите его`,
        6,
      );
      navigate(`/service_registers/${r.id}`);
    },
    onError: (e) => {
      message.error(describeError(e), 8);
      list.refetch();
    },
  });

  if (list.isError) return <LoadProblem error={list.error} onRetry={() => list.refetch()} />;
  const data = list.data;
  const register = data?.register;

  const columns = [
    {
      title: "Операция",
      dataIndex: "number",
      render: (value: string | null, row: BillingOperation) => {
        const kind = operationOf(row.operation);
        const label = kind ? <OperationTag kind={kind}>{row.kind_label}</OperationTag> : <Tag bordered={false}>{row.kind_label}</Tag>;
        return (
          <Space direction="vertical" size={2}>
            {label}
            {row.path ? (
              <Link to={row.path} data-testid={`billing-op-open-${value ?? row.key}`}>
                {value ?? "открыть"}
              </Link>
            ) : (
              value
            )}
          </Space>
        );
      },
    },
    {
      title: "Дата и время",
      dataIndex: "local_date",
      // the operation is shown in the time of its warehouse: the tariff of that day applies
      render: (value: string, row: BillingOperation) => (
        <Tooltip title={`Часовой пояс склада: ${row.timezone}`}>
          <span>
            {formatDate(value)} {row.local_time}
          </span>
        </Tooltip>
      ),
    },
    { title: "Склад", dataIndex: "warehouse_code", render: (v: string | null, row: BillingOperation) => <Tooltip title={row.warehouse_name}>{v ?? "—"}</Tooltip> },
    {
      title: "Обработано",
      dataIndex: "handled",
      render: (handled: BillingOperation["handled"]) => (
        <Space direction="vertical" size={0}>
          {handled.map((h) => (
            <span key={`${h.handling_type}:${h.unit}`}>
              {h.quantity} {h.unit}
              <Typography.Text type="secondary">
                {" "}
                · {h.label.toLowerCase()}
                {h.base_quantity ? `, ${h.base_quantity} ${h.base_unit ?? ""}` : ""}
              </Typography.Text>
            </span>
          ))}
        </Space>
      ),
    },
    {
      title: "Услуги",
      dataIndex: "charges",
      // a long service name wraps inside its tag, so the amount stays in view at 1280 px (browser check 30.10.2026)
      width: 280,
      render: (charges: BillingOperationCharge[]) => (
        <Space size={[4, 4]} wrap>
          {charges.map((c) => (
            <Tag key={`${c.service_id}:${c.date}`} color={STATE[c.state]?.color} style={{ whiteSpace: "normal", marginInlineEnd: 0 }}>
              {c.service_name}: {c.quantity} {c.unit_label.toLowerCase()}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Сумма с налогом",
      dataIndex: "amounts",
      align: "right" as const,
      render: (amounts: BillingOperation["amounts"], row: BillingOperation) =>
        row.selectable ? <b style={{ whiteSpace: "nowrap" }}>{amountsWords(amounts)}</b> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Состояние",
      dataIndex: "reason",
      render: (reason: string | null, row: BillingOperation) => (
        <Space direction="vertical" size={2} style={{ maxWidth: 360 }}>
          {row.selectable ? <Tag color="green">К расчёту</Tag> : <Typography.Text type={row.problem === "in_register" ? "secondary" : "warning"}>{reason}</Typography.Text>}
          {row.registers.map((r) => (
            <Link key={r.id} to={`/service_registers/${r.id}`} data-testid={`billing-op-register-${r.number}`}>
              Реестр {r.number}
            </Link>
          ))}
          {row.notes.map((note) => (
            <Typography.Text key={note} type="secondary" style={{ fontSize: 12 }}>
              {note}
            </Typography.Text>
          ))}
        </Space>
      ),
    },
  ];

  const empty = !partner && !registerId ? (
    <Empty
      data-testid="billing-ops-empty"
      description={
        <>
          <b>Выберите клиента</b>
          <br />
          Здесь появятся его завершённые операции за период: поступления, отгрузки, выполненные задания и ручные услуги
        </>
      }
    />
  ) : (
    <Empty
      data-testid="billing-ops-empty"
      description={
        <>
          <b>Нет завершённых операций клиента за период</b>
          <br />
          {data?.empty_reason ?? "Измените период или склад"}
        </>
      }
    />
  );

  return (
    <div>
      <PageHeader
        title={register ? `Основания реестра ${register.number}` : "Операции к расчёту"}
        back={!!register}
        subtitle={
          register
            ? "Операции, из которых рассчитаны строки реестра: откройте любую, чтобы проверить основание"
            : "Завершённые операции клиента: количество по типу обработки × ставка его тарифа на дату операции. Отметьте нужные и сформируйте реестр услуг — на его основании выставляется счёт"
        }
        extra={
          <Space>
            {register && (
              <Button onClick={() => navigate(`/service_registers/${register.id}`)} data-testid="billing-ops-open-register">
                Открыть реестр
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} aria-label="Обновить" />
          </Space>
        }
      />
      <div className="wms-card" style={{ padding: 16 }}>
        {!register && (
          <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
            <span data-testid="billing-ops-partner">
              <RefSelect
                entity="partners"
                value={partner}
                onChange={(v) => {
                  const next = new URLSearchParams(params);
                  if (v) next.set("partner_id", String(v));
                  else next.delete("partner_id");
                  setParams(next, { replace: true });
                }}
                placeholder="Клиент: название или БИН/ИИН"
                style={{ minWidth: 280 }}
                aria-label="Клиент"
              />
            </span>
            <span data-testid="billing-ops-period">
              <DatePicker.RangePicker
                value={period}
                onChange={(v) => v?.[0] && v?.[1] && setPeriod([v[0], v[1]])}
                format="DD.MM.YYYY"
                allowClear={false}
              />
            </span>
            <span data-testid="billing-ops-warehouse">
              <RefSelect entity="warehouses" value={warehouse} onChange={(v) => setWarehouse(v as string | undefined)} placeholder="Все доступные склады" style={{ minWidth: 200 }} aria-label="Склад" />
            </span>
            {data?.company && <Typography.Text type="secondary">Организация: {data.company.name}</Typography.Text>}
          </Space>
        )}
        {data?.truncated && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Показаны последние 1000 операций периода: сузьте период или выберите склад" />}
        <Table<BillingOperation>
          rowKey="key"
          size="small"
          loading={list.isFetching}
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true, showTotal: (t) => operationsWord(t) }}
          scroll={{ x: true }}
          onRow={(row) => ({ "data-testid": `billing-op-row-${row.number ?? row.key}` }) as React.HTMLAttributes<HTMLElement>}
          expandable={{ expandedRowRender: (row) => <Charges row={row} />, rowExpandable: (row) => row.charges.length > 0 }}
          rowSelection={
            register
              ? undefined
              : {
                  selectedRowKeys: chosen,
                  onChange: (keys) => setSelected(keys.map(String)),
                  preserveSelectedRowKeys: false,
                  getCheckboxProps: (row) => ({ disabled: !row.selectable }),
                  // a flag that cannot be ticked says why right where the hand is
                  renderCell: (_checked, row, _index, node) => (
                    <span data-testid={`billing-op-check-${row.number ?? row.key}`}>{row.selectable ? node : <Tooltip title={row.reason}>{node}</Tooltip>}</span>
                  ),
                }
          }
          locale={{ emptyText: empty }}
        />
        {!register && items.length > 0 && (
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginTop: 12 }}
            data-testid="billing-ops-total"
          >
            <Space direction="vertical" size={0}>
              <span>
                Выбрано: <b>{operationsWord(chosen.length)}</b> на <b data-testid="billing-ops-selected-sum">{amountsWords(chosenAmounts)}</b>
                {/* UX3010-09: the total the customer reads is «количество × ставка», the tax is shown on top of it */}
                {chosen.length > 0 && <Typography.Text type="secondary" data-testid="billing-ops-selected-net"> с налогом; без налога {amountsWords(chosenAmounts, "net")}</Typography.Text>}
              </span>
              <Typography.Text type="secondary">
                Всего к расчёту: {operationsWord(data?.totals.selectable ?? 0)} из {data?.totals.operations ?? 0} на {amountsWords(data?.totals.amounts ?? [])}
              </Typography.Text>
            </Space>
            <Tooltip title={!canRegister ? "Нужно право «Реестры услуг: создание и сбор начислений»" : chosen.length === 0 ? "Отметьте операции флажками" : undefined}>
              <Button
                type="primary"
                icon={<FileDoneOutlined />}
                disabled={!canRegister || chosen.length === 0}
                loading={form.isPending}
                onClick={() => form.mutate()}
                data-testid="billing-ops-register"
              >
                Сформировать реестр{chosen.length ? ` (${operationsWord(chosen.length)}, ${amountsWords(chosenAmounts)})` : ""}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}
